// `released` is the whole-publication release state: an item reaches it only once its edition is
// live, never from its own review status alone.
export type ReviewStatusTone = "notStarted" | "inProgress" | "editing" | "approved" | "released";

export type ReviewStatusBadge = {
  label: string;
  tone: ReviewStatusTone;
};

/**
 * Labels describe the work's current state, not the transition that produced it. `pending` means an
 * editor has not picked the item up yet, which is "In Progress", not pending approval.
 */
const REVIEW_STATUS_CONFIG: Record<string, ReviewStatusBadge> = {
  pending: { label: "In Progress", tone: "inProgress" },
  ready_for_editing: { label: "Editing", tone: "editing" },
  ready_for_translation: { label: "Translation", tone: "editing" },
  ready_for_release: { label: "Approved", tone: "approved" },
};

function humanise(status: string): string {
  const spaced = status.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * An `archived` edition is a past edition that is still published on the site, so it counts as
 * released. `ready_for_release` on the EDITION does not: it means ready to go, not gone.
 */
export function isReleasedEdition(editionStatus: string | null | undefined): boolean {
  return editionStatus === "live" || editionStatus === "archived";
}

export function reviewStatusBadge(
  status: string | null | undefined,
  editionStatus?: string | null,
): ReviewStatusBadge {
  // No editorial content for this edition. This is the one case DEV-2697 §3 specifies as
  // "Not started", and it stays distinct from the fallback below.
  if (!status) {
    return { label: "Not started", tone: "notStarted" };
  }

  // Blue "Released" names content that is actually live. An item still at `pending` in a live
  // edition keeps its own status rather than claiming a publication it never reached.
  if (status === "ready_for_release" && isReleasedEdition(editionStatus)) {
    return { label: "Released", tone: "released" };
  }

  // A status we have no label for reads as itself. Reusing "Not started" here would state that
  // untouched work is untouched, which is false of anything that reached a status at all.
  return REVIEW_STATUS_CONFIG[status] ?? { label: humanise(status), tone: "notStarted" };
}
